import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { loginWithPassword, signupWithPassword } from './auth0Api';
import { setAccessTokenGetter } from '../api/token';
import authCompat from './userfrontCompat';

const AuthContext = createContext(null);
const ACCESS = 'leadgenflow.auth.accessToken';
const ID = 'leadgenflow.auth.idToken';
const EXPIRES = 'leadgenflow.auth.expiresAt';

function decodeJwt(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(atob(part).split('').map(c => `%${('00'+c.charCodeAt(0).toString(16)).slice(-2)}`).join('')));
  } catch { return null; }
}
function readSession() {
  const accessToken = sessionStorage.getItem(ACCESS);
  const idToken = sessionStorage.getItem(ID);
  const expiresAt = Number(sessionStorage.getItem(EXPIRES) || 0);
  if (!accessToken || !idToken || expiresAt <= Date.now() + 5000) return null;
  return { accessToken, idToken, expiresAt, user: decodeJwt(idToken) };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => readSession());

  const activate = useCallback((result) => {
    const expiresAt = Date.now() + Number(result.expires_in || 3600) * 1000;
    const next = { accessToken: result.access_token, idToken: result.id_token, expiresAt, user: decodeJwt(result.id_token) };
    sessionStorage.setItem(ACCESS, next.accessToken);
    sessionStorage.setItem(ID, next.idToken);
    sessionStorage.setItem(EXPIRES, String(expiresAt));
    setAccessTokenGetter(async () => next.accessToken);
    authCompat._set({ user: { ...(next.user || {}), isConfirmed: next.user?.email_verified !== false }, token: next.accessToken, logout: () => clear() });
    setSession(next);
    return next;
  }, []);

  const clear = useCallback(() => {
    sessionStorage.removeItem(ACCESS); sessionStorage.removeItem(ID); sessionStorage.removeItem(EXPIRES);
    setAccessTokenGetter(null); authCompat._set({}); setSession(null);
  }, []);

  React.useEffect(() => {
    if (session?.accessToken && session.expiresAt > Date.now()) {
      setAccessTokenGetter(async () => session.accessToken);
      authCompat._set({ user: { ...(session.user || {}), isConfirmed: session.user?.email_verified !== false }, token: session.accessToken, logout: () => clear() });
    } else {
      clear();
    }
  }, [session, clear]);

  const login = useCallback(async (email, password) => activate(await loginWithPassword(email, password)), [activate]);
  const signup = useCallback(async (data) => activate(await signupWithPassword(data)), [activate]);
  const value = useMemo(() => ({
    user: session?.user || null, isAuthenticated: !!session, isLoading: false,
    login, signup, logout: clear, getAccessToken: async () => session?.accessToken || null,
  }), [session, login, signup, clear]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
